import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  Package, Loader2, RotateCcw, AlertTriangle, Check, History
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const WOAllocations = ({ orderNumber, user }) => {
  const [allocations, setAllocations] = useState([]);
  const [totalActiveBars, setTotalActiveBars] = useState(0);
  const [loading, setLoading] = useState(true);
  const [reversalDialogOpen, setReversalDialogOpen] = useState(false);
  const [selectedAlloc, setSelectedAlloc] = useState(null);
  const [reversalReason, setReversalReason] = useState('');
  const [reversing, setReversing] = useState(false);

  const fetchAllocations = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/material-allocations/by-order/${orderNumber}`);
      setAllocations(res.data.allocations || []);
      setTotalActiveBars(res.data.total_active_bars || 0);
    } catch (err) {
      console.error('Failed to load allocations:', err);
    } finally {
      setLoading(false);
    }
  }, [orderNumber]);

  useEffect(() => {
    if (orderNumber) fetchAllocations();
  }, [orderNumber, fetchAllocations]);

  const openReversalDialog = (alloc) => {
    setSelectedAlloc(alloc);
    setReversalReason('');
    setReversalDialogOpen(true);
  };

  const handleReversal = async () => {
    if (!reversalReason.trim()) {
      toast.error('Please provide a reason for reversal');
      return;
    }
    setReversing(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/material-allocations/${selectedAlloc.allocation_id}/reverse`,
        { reversed_by: user?.email || 'System', reason: reversalReason.trim() }
      );
      toast.success(res.data.message);
      setReversalDialogOpen(false);
      fetchAllocations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reversal failed');
    } finally {
      setReversing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="w-5 h-5 animate-spin text-cyan-400" />
      </div>
    );
  }

  if (allocations.length === 0) {
    return (
      <div className="bg-slate-900/60 border border-slate-700 rounded-lg p-6" data-testid="wo-allocations-section">
        <div className="flex items-center gap-2 mb-3">
          <Package className="w-5 h-5 text-cyan-400" />
          <h3 className="text-sm font-bold text-white uppercase tracking-wide">Raw Material Allocations</h3>
        </div>
        <p className="text-slate-500 text-sm">No raw materials have been allocated to this works order yet.</p>
      </div>
    );
  }

  return (
    <>
      <div className="bg-slate-900/60 border border-slate-700 rounded-lg overflow-hidden" data-testid="wo-allocations-section">
        <div className="p-4 border-b border-slate-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Package className="w-5 h-5 text-cyan-400" />
            <h3 className="text-sm font-bold text-white uppercase tracking-wide">Raw Material Allocations</h3>
          </div>
          <div className="text-right">
            <p className="text-xs text-slate-500 uppercase">Total Active Bars</p>
            <p className="text-lg font-bold text-cyan-400 font-mono" data-testid="total-active-bars">{totalActiveBars}</p>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-700 bg-slate-800/50">
                <th className="text-left py-2 px-3 text-[10px] text-slate-400 uppercase">Material</th>
                <th className="text-left py-2 px-3 text-[10px] text-slate-400 uppercase">Cast/Heat No</th>
                <th className="text-right py-2 px-3 text-[10px] text-slate-400 uppercase">Dia (mm)</th>
                <th className="text-right py-2 px-3 text-[10px] text-slate-400 uppercase">Bars</th>
                <th className="text-left py-2 px-3 text-[10px] text-slate-400 uppercase">Booked</th>
                <th className="text-left py-2 px-3 text-[10px] text-slate-400 uppercase">By</th>
                <th className="text-left py-2 px-3 text-[10px] text-slate-400 uppercase">Status</th>
                <th className="text-right py-2 px-3 text-[10px] text-slate-400 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {allocations.map((alloc) => {
                const isReversed = alloc.status === 'reversed';
                return (
                  <tr 
                    key={alloc.allocation_id} 
                    className={`border-b border-slate-800 ${isReversed ? 'opacity-60 bg-slate-900/30' : 'hover:bg-slate-800/50'}`}
                    data-testid={`allocation-row-${alloc.allocation_id}`}
                  >
                    <td className="py-2 px-3 text-white text-sm">{alloc.material_grade}</td>
                    <td className="py-2 px-3 text-purple-400 font-mono text-sm">{alloc.cast_number || '-'}</td>
                    <td className="py-2 px-3 text-right text-cyan-400 font-mono text-sm">{alloc.diameter || '-'}</td>
                    <td className="py-2 px-3 text-right text-white font-bold font-mono">{alloc.bars_booked}</td>
                    <td className="py-2 px-3 text-slate-400 text-xs">
                      {alloc.booked_at ? new Date(alloc.booked_at).toLocaleDateString('en-ZA') : '-'}
                      <br />
                      <span className="text-slate-500">{alloc.booked_at ? new Date(alloc.booked_at).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' }) : ''}</span>
                    </td>
                    <td className="py-2 px-3 text-slate-400 text-xs">{alloc.booked_by}</td>
                    <td className="py-2 px-3">
                      {isReversed ? (
                        <div>
                          <span className="text-[10px] px-1.5 py-0.5 bg-red-950 text-red-400 rounded">Reversed</span>
                          {alloc.reversal_reason && (
                            <p className="text-[10px] text-slate-400 mt-1 max-w-[120px] truncate" title={alloc.reversal_reason}>
                              {alloc.reversal_reason}
                            </p>
                          )}
                          {alloc.reversed_by && (
                            <p className="text-[10px] text-slate-500 mt-0.5">by {alloc.reversed_by}</p>
                          )}
                        </div>
                      ) : (
                        <span className="text-[10px] px-1.5 py-0.5 bg-green-950 text-green-400 rounded">Active</span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-right">
                      {!isReversed && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openReversalDialog(alloc)}
                          className="h-7 px-2 text-amber-400 hover:bg-amber-500/10"
                          title="Reverse Allocation"
                          data-testid={`reverse-alloc-${alloc.allocation_id}`}
                        >
                          <RotateCcw className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Reversal Dialog */}
      <Dialog open={reversalDialogOpen} onOpenChange={setReversalDialogOpen}>
        <DialogContent className="bg-slate-900 border-amber-500/30 max-w-md" data-testid="reversal-dialog">
          <DialogHeader>
            <DialogTitle className="text-white uppercase tracking-wide flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-amber-400" />
              Reverse Allocation
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">
              This will return {selectedAlloc?.bars_booked} bars to available inventory.
              A reason is required for audit compliance.
            </DialogDescription>
          </DialogHeader>
          
          {selectedAlloc && (
            <div className="space-y-4">
              <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-3 grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-[10px] text-slate-500 uppercase">Allocation ID</p>
                  <p className="text-white font-mono text-xs">{selectedAlloc.allocation_id}</p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-500 uppercase">Cast Number</p>
                  <p className="text-purple-400 font-mono">{selectedAlloc.cast_number || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-500 uppercase">Bars Booked</p>
                  <p className="text-white font-bold font-mono">{selectedAlloc.bars_booked}</p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-500 uppercase">Booked By</p>
                  <p className="text-slate-400 text-xs">{selectedAlloc.booked_by}</p>
                </div>
              </div>
              
              <div>
                <Label className="text-slate-400 uppercase text-xs">Reason for Reversal *</Label>
                <Textarea
                  value={reversalReason}
                  onChange={(e) => setReversalReason(e.target.value)}
                  className="bg-slate-800 border-slate-700 min-h-[80px]"
                  placeholder="Explain why this allocation is being reversed..."
                  data-testid="reversal-reason-input"
                />
              </div>

              <div className="flex items-center gap-2 bg-amber-950/30 border border-amber-500/20 rounded p-2">
                <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />
                <p className="text-amber-300 text-xs">
                  The original allocation record will be preserved for audit purposes. 
                  This action will be logged.
                </p>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setReversalDialogOpen(false)}
                  className="border-slate-600"
                  data-testid="cancel-reversal-btn"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleReversal}
                  disabled={reversing || !reversalReason.trim()}
                  className="bg-amber-600 hover:bg-amber-500"
                  data-testid="confirm-reversal-btn"
                >
                  {reversing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RotateCcw className="w-4 h-4 mr-2" />}
                  {reversing ? 'Reversing...' : 'Confirm Reversal'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default WOAllocations;
